import { useState, useEffect, useCallback } from "react";
import axios from "axios";

const useActivityFeed = (
  feedType,
  diverId,
  divesiteId,
  filter,
  currentUser,
  userObjectId
) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);

  const BASE_URL = "/api/activities";

  const getEndpoint = useCallback(() => {
    switch (feedType) {
      case "personal":
        if (!userObjectId) {
          setError("User ID not available for personal feed.");
          return null;
        }
        return `${BASE_URL}/personal/${userObjectId}`;
      case "global":
        return `${BASE_URL}/global`;
      case "divesite":
        if (!divesiteId) {
          setError("Divesite ID not provided for divesite feed.");
          return null;
        }
        return `${BASE_URL}/divesite/${divesiteId}`;
      default:
        return `${BASE_URL}/global`;
    }
  }, [feedType, diverId, divesiteId, userObjectId]);

  const fetchActivities = useCallback(
    async (pageNum = 1, isRefresh = false) => {
      const endpoint = getEndpoint();
      if (!endpoint) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const token = currentUser ? await currentUser.getIdToken() : null;
        if (!token) {
          setError("Authentication token not available.");
          setLoading(false);
          return;
        }

        const params = {
          page: pageNum,
          limit: 20,
          ...(filter && { activityType: filter }),
        };

        const response = await axios.get(endpoint, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params,
        });

        const data = response.data;

        if (isRefresh || pageNum === 1) {
          setActivities(data.activities);
        } else {
          setActivities((prev) => [...prev, ...data.activities]);
        }

        setHasMore(pageNum < (data.totalPages || 1));
      } catch (err) {
        console.error("Error fetching activities:", err);
        setError(err.response?.data?.message || "Failed to fetch activities.");
      } finally {
        setLoading(false);
      }
    },
    [getEndpoint, filter, currentUser]
  );

  useEffect(() => {
    if (feedType === "personal" && !userObjectId) {
      setLoading(false);
      setError("Please log in to view your personal activity feed.");
      setActivities([]);
      return;
    }
    setPage(1);
    fetchActivities(1, true);
  }, [
    fetchActivities,
    feedType,
    diverId,
    divesiteId,
    filter,
    currentUser,
    userObjectId,
  ]);

  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchActivities(nextPage);
    }
  }, [loading, hasMore, page, fetchActivities]);

  const refreshFeed = useCallback(() => {
    setPage(1);
    fetchActivities(1, true);
  }, [fetchActivities]);

  return {
    activities,
    loading,
    error,
    hasMore,
    page,
    loadMore,
    refreshFeed,
  };
};

export default useActivityFeed;
